import React from 'react';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import ExpandingTextArea from 'react-expanding-textarea';
import Print from 'Print';

// Services & Helpers
import API from 'API';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import CurrencyInput from 'components/common/CurrencyInput';
import DatePicker from 'components/common/DatePicker';

//-----------------------------------------------------------------

class EditInvoiceModal extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
        };
    }

    open(opt) {
        return new Promise((resolve) => {
            this.resolve = resolve;
            this.setState({
                isOpen: true,
                isLoading: true,
                id: null,
                ...opt
            }, () => {
                this.load();
            });
        });
    }

    async load() {
        const invoice = await API.call('invoice/get/' + this.state.id);
        const paymentMethods = await API.call('payment-method/list');
        this.setState({
            invoice,
            paymentMethods,
            isLoading: false
        });
    }

    close() {
        this.setState({ isOpen: false });
    }

    updateFields(newValues) {
        const invoice = { ...this.state.invoice };
        for (let fieldName in newValues) {
            const value = newValues[fieldName];
            invoice[fieldName] = value;
        }
        this.updateBalance(invoice);
        this.setState({ invoice });
    }

    addPayment() {
        const { invoice } = this.state;
        const payments = [...invoice.payments];
        let id = 0;
        payments.forEach(ip => {
            if (ip.id < id) id = ip.id;
        });
        id--;
        payments.push({
            id,
            date: moment().startOf('day').toDate()
        });
        this.focusInput = `payment-${id}`;
        this.updateFields({ payments });
        this.isChanged = true;
    }

    updatePayment(id, fields) {
        const payments = [...this.state.invoice.payments];
        const index = payments.findIndex(ip => ip.id == id);
        const payment = { ...payments[index] };
        for (let fieldName in fields) {
            const value = fields[fieldName];
            payment[fieldName] = value;
        }
        payments[index] = payment;
        this.updateFields({ payments });
        this.isChanged = true;
    }

    confirmRemovePayment(id) {
        const confirm = window.confirm('Are you sure you want to remove this payment?');
        if (confirm) {
            const payments = [...this.state.invoice.payments];
            const index = payments.find(p => p.id == id);
            payments.splice(index, 1);
            this.updateFields({ payments });
            this.isChanged = true;
        }
    }

    async save() {
        this.setState({ isLoading: true });
        await API.call('invoice/save', this.state.invoice);
        this.setState({ isLoading: false });
    }

    async saveAndClose() {
        await this.save();
        this.setState({ isOpen: false });
        this.resolve();
    }

    updateBalance(invoice) {
        invoice.balance = invoice.value;
        invoice.payments.forEach(ip => {
            invoice.balance -= (Number(ip.amount) || 0);
        });
    }

    //--------------------------------------------------------------------------

    render() {
        const { 
            isOpen,
            isLoading,
            invoice
        } = this.state;

        if (!isOpen) {
            return null;
        }

        return (
            <Modal show onHide={() => this.close()} className="edit-invoice-modal" backdrop="static" keyboard={false}>
                <form onSubmit={e => { e.preventDefault(); e.stopPropagation(); this.saveAndClose() }}>
                    <Modal.Body>

                        {this.renderInner()}

                    </Modal.Body>
                    {!isLoading &&
                        <Modal.Footer>
                            <button type="button" className="btn btn-secondary" onClick={() => this.close()}>
                                Close
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={async () => {
                                await this.save();
                                await Print.url('/api/invoice/get-pdf/' + invoice.id, { isInline: true });
                            }}>
                                Print
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={async () => {
                                await this.save();
                                await Print.url('/api/invoice/get-pdf/' + invoice.id, { isInline: false });
                            }}>
                                Download
                            </button>
                            <button type="submit" className="btn btn-primary ms-auto">
                                Save
                            </button>
                        </Modal.Footer>
                    }
                </form>
            </Modal>
        );
    }

    renderInner() {
        const {
            isLoading,
            payment
        } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>

            {this.renderPayments()}

        </>);
    }

    renderPayments() {
        const { invoice, paymentMethods } = this.state;

        let table;
        if (invoice.payments.length == 0) {
            table = (
                <p className="form-control-plaintext">
                    No payments added
                </p>
            );
        } else {
            table = (
                <table className="table table-bordered invoice-payments-table mb-0">
                    <thead>
                        <tr>
                            <th className="date-col">Date</th>
                            <th className="amount-col">Amount</th>
                            <th className="method-col">Method</th>
                            <th className="deposit-col">Deposit</th>
                            <th className="actions-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoice.payments.map(ip =>
                            <React.Fragment key={ip.id}>
                                <tr>
                                    <td className="date-col">
                                        <DatePicker
                                            className="form-control"
                                            wrapperClassName="date-picker"
                                            value={ip.date ? new Date(ip.date) : null}
                                            onChange={date => this.updatePayment(ip.id, { date })}
                                        />
                                    </td>
                                    <td className="amount-col">
                                        <CurrencyInput
                                            className="form-control"
                                            value={ip.amount || ''}
                                            autoFocus={this.focusInput == `payment-${ip.id}`}
                                            onValueChange={(values) => {
                                                const amount = Number(values.value) || '';
                                                this.updatePayment(ip.id, { amount });
                                            }}
                                        />
                                    </td>
                                    <td className="method-col">
                                        <select
                                            className="form-control"
                                            value={ip.paymentMethodID || ''}
                                            onChange={e => this.updatePayment(ip.id, { paymentMethodID: e.target.value })}
                                        >
                                            <option value="">(Select)</option>
                                            {paymentMethods.map(pm =>
                                                <option key={pm.id} value={pm.id}>
                                                    {pm.name}
                                                </option>
                                            )}
                                        </select>
                                    </td>
                                    <td className="deposit-col">
                                        <input
                                            type="checkbox"
                                            className="form-control"
                                            checked={ip.isDeposit || false}
                                            onChange={e => this.updatePayment(ip.id, { isDeposit: e.target.checked })}
                                        />
                                    </td>
                                    <td className="actions-col">

                                        <button type="button" className="btn btn-danger" onClick={() => this.confirmRemovePayment(ip.id)}>
                                            <span className="fa fa-times" />
                                        </button>

                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={5} className="description-col">
                                        <ExpandingTextArea
                                            className="form-control"
                                            placeholder="Payment description/notes"
                                            rows={1}
                                            key={ip.description || ''}
                                            defaultValue={ip.description || ''}
                                            onBlur={e => this.updatePayment(ip.id, { description: e.target.value })}
                                        />
                                    </td>
                                </tr>
                            </React.Fragment>
                        )}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>Balance</td>
                            <td className="amount-col">
                                {TextHelpers.formatCurrency(invoice.balance)}
                            </td>
                            <td colSpan="3"></td>
                        </tr>
                    </tfoot>
                </table>
            );
        }

        return (
            <div className="form-group mb-0">
                <label className="form-label mb-2">
                    Payments

                    <button type="button" className="btn btn-primary ms-auto" onClick={() => this.addPayment()}>
                        <span className="fa-solid fa-plus" />{' '}
                        Add Payment
                    </button>
                </label>
                {table}
            </div>
        );
    }
}

export default EditInvoiceModal;